/**
 * Helper functions for reactions feature
 */

/**
 * Get the reaction emoji by reaction type ID
 * @param {number} reactionTypeId - The reaction type ID
 * @param {Array} reactions - Array of available reactions
 * @returns {string|null} - The emoji or null if not found
 */
export const getReactionEmoji = (reactionTypeId, reactions = []) => {
  if (!reactionTypeId || !reactions.length) return null;
  const reaction = reactions.find((r) => r.id === reactionTypeId);
  return reaction?.emoji || null;
};

/**
 * Get the reaction name by reaction type ID
 * @param {number} reactionTypeId - The reaction type ID
 * @param {Array} reactions - Array of available reactions
 * @returns {string|null} - The reaction name or null if not found
 */
export const getReactionName = (reactionTypeId, reactions = []) => {
  if (!reactionTypeId || !reactions.length) return null;
  const reaction = reactions.find((r) => r.id === reactionTypeId);
  return reaction?.name || null;
};

/**
 * Format total reaction count
 * @param {number} totalCount - Total reaction count
 * @returns {string|null} - Formatted count or null if 0
 */
export const formatReactionCount = (totalCount) => {
  if (!totalCount || totalCount === 0) return null;
  if (totalCount >= 1000) {
    return (totalCount / 1000).toFixed(1) + "K";
  }
  return totalCount.toString();
};

/**
 * Get the current user's reaction from reactions data
 * Handles both API response format and post/comment format
 * @param {Object} reactionsData - Reactions data from API or post/comment
 * @returns {Object|null} - Current user reaction or null
 */
export const getCurrentUserReaction = (reactionsData) => {
  if (!reactionsData) return null;

  // Handle API response format (from react endpoint): data.user_reaction
  if (reactionsData.user_reaction) {
    return {
      reaction_type_id: reactionsData.user_reaction.id,
      emoji: reactionsData.user_reaction.emoji,
      name: reactionsData.user_reaction.name,
    };
  }

  // Handle post/comment format: reactions.current_user_reaction
  if (reactionsData.current_user_reaction) {
    return reactionsData.current_user_reaction;
  }

  return null;
};

/**
 * Get reaction summary from reactions data
 * Handles both API response format and post/comment format
 * @param {Object} reactionsData - Reactions data from API or post/comment
 * @returns {Array} - Array of reaction summaries
 */
export const getReactionSummary = (reactionsData) => {
  if (!reactionsData) return [];

  // Handle API response format (from react endpoint): data.reactions array
  if (Array.isArray(reactionsData.reactions)) {
    return reactionsData.reactions.map((r) => ({
      reaction_type_id: r.reaction_type?.id || r.reaction_type_id,
      emoji: r.reaction_type?.emoji || r.emoji,
      name: r.reaction_type?.name || r.name,
      count: r.count || 0,
    }));
  }

  // Handle post/comment format: reactions.summary
  if (reactionsData.summary) {
    return reactionsData.summary;
  }

  return [];
};

/**
 * Calculate total reaction count from summary or reactionsData
 * @param {Array} summary - Reaction summary array (optional)
 * @param {Object} reactionsData - Reactions data (optional)
 * @returns {number} - Total count
 */
export const getTotalReactionCount = (summary = [], reactionsData = null) => {
  // If reactionsData has total_count, use it
  if (reactionsData?.total_count !== undefined) {
    return reactionsData.total_count;
  }

  // Otherwise calculate from summary
  return summary.reduce((total, reaction) => total + (reaction.count || 0), 0);
};

/**
 * Get the icon to display based on current user reaction
 * @param {Object} currentUserReaction - Current user's reaction
 * @param {boolean} liked - Legacy liked state (for backward compatibility)
 * @returns {string} - Icon path
 */
export const getReactionIcon = (currentUserReaction, liked = false) => {
  // If new reactions system is used
  if (currentUserReaction) {
    // Return filled like icon for any reaction (we'll show emoji separately)
    return "icons/post-liked.svg";
  }
  // Legacy like system
  return liked ? "icons/post-liked.svg" : "icons/post-like.svg";
};
